import {
  InputBirthdayProps,
  InputProps,
  SelectProps,
  InputAddressProps,
  InputAddressData,
  TextSizes,
  InputPhoneProps,
  CheckboxProps,
} from '@afterpaytouch/core'
import { CardBrand, PaymentType } from '@afterpay/types'
import { FunctionComponent, ReactNode } from 'react'
import { RegisterOptions } from 'react-hook-form'

export enum FieldType {
  Component = 'Component',
  Input = 'Input',
  Dropdown = 'Dropdown',
  DateOfBirth = 'DateOfBirth',
  Address = 'Address',
  Date = 'Date',
  InputCluster = 'InputCluster',
  Card = 'Card',
  Phone = 'Phone',
  Checkbox = 'Checkbox',
  Password = 'Password',
  Number = 'Number',
  Money = 'Money',
}

export interface CommonFieldSchema<Props> {
  type: FieldType
  name?: string
  show?: boolean | ((values?: any) => boolean)
  options?: RegisterOptions
  props?: Props & { key?: string; errorText?: any }
  inline?: boolean | number
  testNameSpace?: string
  disabled?: boolean
}

export interface DropdownItems {
  key: string
  label: string
}

export type ValidationMode = 'onBlur' | 'onChange'

export interface CardFieldSchema extends CommonFieldSchema<{}> {
  type: FieldType.Card
  fieldTitle: string
  fieldTitleSize: TextSizes
  acceptableCardBrands: CardBrand[]
  showCardHolderNameField: boolean
  defaultValues: CardDefaultValue
  lastPaymentDate?: Date | null
  errorMessages: ErrorMessages
  labels: CardTranslationLabel
  showHeading?: boolean
  validationMode?: ValidationMode
  blockedBins?: string[]
  paymentType?: PaymentType
  showCardIcon?: boolean
  customizedHeading?: ReactNode
}

export interface CardTranslationLabel {
  cardHolderName?: string
  expiryDate: string
  cardNumber: string
  cvv: string
}

export interface ErrorMessages {
  textSize: TextSizes
  cardHolderName: {
    empty: string
  }
  expiryDate: {
    empty: string
    past: string
    expireSoon?: string
    expireInLimitMonths?: string
  }
  cardNumber: {
    invalid: string
    brandNotAccepted: string | ((brand: CardBrand) => string)
    binNotAccepted?: string | ((cardNumber: string) => string)
  }
  cvv: {
    invalid: string
  }
}

export enum AmexNumberFormat {
  NUMBER_FORMAT = '0000  000000  00000',
  CVV_FORMAT = '0000',
}

export enum StandardNumberFormat {
  NUMBER_FORMAT = '0000  0000  0000  0000',
  CVV_FORMAT = '000',
}

export interface AmexCreditCardFormats {
  NUMBER_FORMAT: AmexNumberFormat.NUMBER_FORMAT
  CVV_FORMAT: AmexNumberFormat.CVV_FORMAT
}

export interface StandardCreditCardFormats {
  NUMBER_FORMAT: StandardNumberFormat.NUMBER_FORMAT
  CVV_FORMAT: StandardNumberFormat.CVV_FORMAT
}

export type CardFormErrors = {
  [key in CardFormField]?: string
}

export enum CardFormField {
  cardHolderName = 'cardHolderName',
  expiryDate = 'expiryDate',
  cardNumber = 'cardNumber',
  cvv = 'cvv',
}

export type CardDefaultValue = {
  [key in CardFormField]: string
}

type InputControlledProps = 'name' | 'required' | 'pattern' | 'errorText' | 'clusterPosition'
export type InputFieldProps = Omit<InputProps, InputControlledProps>
export interface InputFieldSchema extends CommonFieldSchema<InputFieldProps> {
  type: FieldType.Input
}

type NumberControlledProps = 'name' | 'required' | 'pattern' | 'errorText' | 'clusterPosition'
export type NumberFieldProps = Omit<InputProps, NumberControlledProps>
export interface NumberFieldSchema extends CommonFieldSchema<NumberFieldProps> {
  type: FieldType.Number
}

type MoneyControlledProps = 'name' | 'required' | 'pattern' | 'errorText' | 'clusterPosition' | 'currencySymbol'
export type MoneyFieldProps = Omit<InputProps, MoneyControlledProps>
export interface MoneyFieldSchema extends CommonFieldSchema<MoneyFieldProps> {
  type: FieldType.Money
  currencySymbol?: string
}

type PasswordControlledProps = 'name' | 'required' | 'pattern' | 'errorText' | 'clusterPosition'
export type PasswordFieldProps = Omit<InputProps, PasswordControlledProps>
export interface PasswordFieldSchema extends CommonFieldSchema<PasswordFieldProps> {
  type: FieldType.Password
}

type DropdownControlledProps = 'name' | 'children' | 'errorText'
export interface DropdownFieldSchema extends CommonFieldSchema<Omit<SelectProps, DropdownControlledProps>> {
  type: FieldType.Dropdown
  items: DropdownItems[]
}

type DateOfBirthControlledProps = 'name' | 'errorText' | 'minAge' | 'maxAge'
interface DateOfBirthValidation {
  value: number
  message: string
}
export interface DateOfBirthFieldSchema extends CommonFieldSchema<Omit<InputBirthdayProps, DateOfBirthControlledProps>> {
  type: FieldType.DateOfBirth
  minAge?: DateOfBirthValidation
  maxAge?: DateOfBirthValidation
}

type AddressControlledProps = 'name' | 'errorText' | 'apiKey'
export interface AddressFieldSchema extends CommonFieldSchema<Omit<InputAddressProps, AddressControlledProps>> {
  type: FieldType.Address
  apiKey: string
  showFullPage?: boolean
  validateOnLoad?: boolean
}

type PhoneControlledProps = 'name' | 'errorText'
export interface PhoneFieldSchema extends CommonFieldSchema<Omit<InputPhoneProps, PhoneControlledProps>> {
  type: FieldType.Phone
  invalid?: string
  validateOnLoad?: boolean
}

type CheckboxControlledProps = 'name' | 'errorText'
export interface CheckboxFieldSchema extends CommonFieldSchema<Omit<CheckboxProps, CheckboxControlledProps>> {
  type: FieldType.Checkbox
}

export interface CustomSchema extends CommonFieldSchema<{}> {
  type: FieldType.Component
  Component: FunctionComponent
}

type DateControlledProps = 'name' | 'required' | 'pattern' | 'errorText' | 'clusterPosition' | 'format'
export interface DateFieldSchema extends CommonFieldSchema<Omit<InputProps, DateControlledProps>> {
  type: FieldType.Date
  format?: string
}

export type Field =
  | Omit<InputFieldSchema, 'name'>
  | Omit<DropdownFieldSchema, 'name'>
  | Omit<DateOfBirthFieldSchema, 'name'>
  | Omit<AddressFieldSchema, 'name'>
  | CustomSchema
  | Omit<DateFieldSchema, 'name'>
  | Omit<CardFieldSchema, 'name'>
  | Omit<PhoneFieldSchema, 'name'>
  | Omit<CheckboxFieldSchema, 'name'>
  | Omit<PasswordFieldSchema, 'name'>
  | Omit<NumberFieldSchema, 'name'>
  | Omit<MoneyFieldSchema, 'name'>
// | CheckboxFieldSchema
// | RadioFieldSchema

interface InputClusterValues {
  [prop: string]: string | number
}

export type SchemaConstraint<T> = {
  [P in keyof T]: string | number | boolean | InputAddressData | InputClusterValues
}

export type Schema<T extends SchemaConstraint<T>> = {
  [P in keyof T]: Field
}
